import React from 'react';
import { Download } from 'lucide-react';
import {
  resumeMeta,
  resumeSummary,
  resumeEducation,
  resumeSkills,
  resumeExperience,
  resumeEmployment
} from '../../data/mock';

/**
 * Resume — on-screen preview mirrors the exact content of the downloadable
 * PDF. The "Download PDF" button serves the pre-built PDF stored at
 * /resume/Saksham_Shukla_Resume.pdf so the user always gets the intended
 * design intact (no runtime generation).
 */
const PDF_HREF = '/resume/Saksham_Shukla_Resume.pdf';
const PDF_FILENAME = 'Saksham_Shukla_Resume.pdf';

export default function Resume() {
  return (
    <section className="xl:h-full w-full xl:overflow-hidden px-4 sm:px-6 md:px-10 lg:px-12 py-3 md:py-5 flex flex-col">
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] sm:text-[13px]" style={{ color: 'var(--amber)', letterSpacing: '0.15em' }}>
        <span className="font-semibold">RESUME</span>
        <span className="flex-1 min-w-[24px] h-px" style={{ background: 'rgba(245,165,36,0.2)' }} />
        <a
          href={PDF_HREF}
          download={PDF_FILENAME}
          data-testid="resume-download-btn"
          className="press inline-flex items-center gap-2 text-[12px] normal-case tracking-normal px-3 py-1.5 rounded-sm"
          style={{
            border: '1px solid var(--amber)',
            background: 'var(--amber)',
            color: '#0a0704',
            fontWeight: 600
          }}
        >
          <Download size={13} strokeWidth={2.2} />
          Download PDF
        </a>
      </div>

      <article
        className="glass-card rounded-sm px-5 sm:px-8 py-6 flex flex-col xl:flex-1 xl:min-h-0 xl:overflow-y-auto"
        style={{ border: '1px solid var(--amber)', overflowX: 'hidden' }}
        data-testid="resume-preview"
      >
        {/* Identity */}
        <header className="pb-5 mb-6" style={{ borderBottom: '1px solid rgba(245,165,36,0.32)' }}>
          <h1 className="glow font-semibold" style={{ color: 'var(--amber)', letterSpacing: '0.03em', fontSize: '30px', lineHeight: 1.05 }}>
            {resumeMeta.name}
          </h1>
          <div className="mt-1 text-[13px] uppercase" style={{ color: 'rgba(245,165,36,0.75)', letterSpacing: '0.22em' }}>
            {resumeMeta.title}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[11.5px]" style={{ color: 'rgba(245,165,36,0.72)' }}>
            <span>{resumeMeta.location}</span>
            <span aria-hidden>·</span>
            <a href={resumeMeta.portfolio} target="_blank" rel="noopener noreferrer" className="link-amber">{stripProtocol(resumeMeta.portfolio)}</a>
            <span aria-hidden>·</span>
            <a href={`mailto:${resumeMeta.email}`} className="link-amber">{resumeMeta.email}</a>
            <span aria-hidden>·</span>
            <a href={`tel:${resumeMeta.phone.replace(/\s/g, '')}`} className="link-amber">{resumeMeta.phone}</a>
            <span aria-hidden>·</span>
            <a href={resumeMeta.linkedin} target="_blank" rel="noopener noreferrer" className="link-amber">{stripProtocol(resumeMeta.linkedin)}</a>
          </div>
        </header>

        <SectionTitle n="01">Profile</SectionTitle>
        <p className="text-[13px] leading-relaxed mb-7 max-w-3xl" style={{ color: 'rgba(245,165,36,0.88)' }}>
          {resumeSummary}
        </p>

        <SectionTitle n="02">Featured Project</SectionTitle>
        <div className="mb-7 flex flex-col gap-5">
          {resumeExperience.map((e, i) => (
            <RoleBlock key={`featured-${i}`} role={e} />
          ))}
        </div>

        <SectionTitle n="03">Experience</SectionTitle>
        <div className="mb-7 flex flex-col gap-5">
          {resumeEmployment.map((e, i) => (
            <RoleBlock key={`emp-${i}`} role={e} />
          ))}
        </div>

        <SectionTitle n="04">Education</SectionTitle>
        <div className="mb-7">
          {resumeEducation.map((ed, i) => {
            const meta = [ed.period, ed.location].filter(Boolean).join(' · ');
            return (
              <div key={i}>
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5">
                  <div className="text-[15px] font-semibold" style={{ color: 'var(--amber)', letterSpacing: '0.01em' }}>
                    {ed.degree}
                  </div>
                  {meta && (
                    <div className="text-[11px] whitespace-nowrap" style={{ color: 'rgba(245,165,36,0.55)', fontFeatureSettings: '"tnum"' }}>
                      {meta}
                    </div>
                  )}
                </div>
                {ed.school && (
                  <div className="text-[12.5px] mt-0.5" style={{ color: 'rgba(245,165,36,0.75)' }}>
                    {ed.school}
                  </div>
                )}
                {ed.details && (
                  <div className="text-[12px] leading-relaxed mt-1.5" style={{ color: 'rgba(245,165,36,0.7)' }}>
                    {ed.details}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <SectionTitle n="05">Skills</SectionTitle>
        <div className="grid grid-cols-1 gap-y-2.5">
          {Object.entries(resumeSkills).map(([group, items]) => (
            <div key={group} className="flex gap-4">
              <div className="w-[150px] shrink-0 text-[10px] uppercase pt-1" style={{ color: 'rgba(245,165,36,0.55)', letterSpacing: '0.18em' }}>
                {group}
              </div>
              <div className="text-[12.5px] leading-relaxed" style={{ color: 'rgba(245,165,36,0.88)' }}>
                {items.join(' · ')}
              </div>
            </div>
          ))}
        </div>
      </article>
    </section>
  );
}

function SectionTitle({ n, children }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <span
        aria-hidden
        style={{ display: 'inline-block', width: 14, height: 2, background: 'var(--amber)', boxShadow: '0 0 8px rgba(245,165,36,0.55)' }}
      />
      <span className="text-[10px] font-mono opacity-55" style={{ color: 'var(--amber)', letterSpacing: '0.2em' }}>
        {n}
      </span>
      <h2 className="text-[13px] font-semibold uppercase" style={{ color: 'var(--amber)', letterSpacing: '0.28em' }}>
        {children}
      </h2>
      <span className="flex-1 h-px" style={{ background: 'rgba(245,165,36,0.15)' }} />
    </div>
  );
}

function RoleBlock({ role }) {
  return (
    <div className="relative pl-4" style={{ borderLeft: '1px solid rgba(245,165,36,0.22)' }}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5">
        <div className="text-[15px] font-semibold" style={{ color: 'var(--amber)', letterSpacing: '0.01em' }}>
          {role.role}
        </div>
        <div className="text-[11px] whitespace-nowrap" style={{ color: 'rgba(245,165,36,0.55)', fontFeatureSettings: '"tnum"' }}>
          {role.period} · {role.location}
        </div>
      </div>
      <div className="text-[12.5px] mt-0.5" style={{ color: 'rgba(245,165,36,0.75)' }}>
        {role.company}
        {role.url && (
          <>
            {'  '}
            <a href={role.url} target="_blank" rel="noopener noreferrer" className="link-amber">
              ↗ {stripProtocol(role.url)}
            </a>
          </>
        )}
      </div>
      {role.intro && (
        <p className="mt-2 text-[12.5px] leading-relaxed" style={{ color: 'rgba(245,165,36,0.88)' }}>
          {role.intro}
        </p>
      )}
      <ul className="mt-2 space-y-1 text-[12px] leading-relaxed" style={{ color: 'rgba(245,165,36,0.8)' }}>
        {role.bullets.map((b, k) => (
          <li key={k} className="flex gap-2">
            <span aria-hidden style={{ color: 'rgba(245,165,36,0.4)' }}>▸</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function stripProtocol(url) {
  return url.replace(/^https?:\/\//, '').replace(/^mailto:/, '').replace(/\/$/, '');
}
